import { AlertCircle, Award, ChevronDown, Volume2, X } from 'lucide-react'
import React, { useState } from 'react'

import type { GradeRecordingResponse, MultiPersonaResponse, SegmentResponse, WordDetail, WordResponse } from '@/api/type'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { speechAligner } from '@/lib/speech-aligner'

import { ControlButton } from '../playback-controls/control-button'
import { PronunciationComparison } from './pronunciation-comparison'

type PersonaType = 'coaching' | 'prosody' | 'analytical' | 'liaison'

const PERSONA_CONFIG = {
    coaching: { label: 'コーチング', icon: '👨‍🏫', color: 'indigo' },
    prosody: { label: '韻律特化', icon: '🎵', color: 'purple' },
    analytical: { label: 'スパルタ', icon: '📊', color: 'pink' },
    liaison: { label: 'リエゾン', icon: '🔗', color: 'cyan' },
} as const

const PERSONA_DESCRIPTIONS = {
    coaching: '総合的な評価と励ましを含む優しいコーチングスタイル。良かった点を認めつつ、改善ポイントを具体的な練習法とともに提示します。',
    prosody: 'リズム、イントネーション、間の取り方など韻律に特化した分析。音楽的・演技的な視点から自然な英語の話し方を指導します。',
    analytical: '誤発音、省略、挿入などを細かく指摘するスパルタ分析型。全てのエラーを徹底的にリスト化し、改善を促します。',
    liaison: '音の連結（リンキング）に特化した分析。単語間の滑らかな繋がりを評価し、ネイティブのような自然な発音を目指します。',
} as const

// AIフィードバックパネルコンポーネント
const AIFeedbackPanel = ({ feedback }: { feedback: MultiPersonaResponse }) => {
    const [selectedPersona, setSelectedPersona] = useState<PersonaType>(() => {
        const saved = localStorage.getItem('ai_feedback_persona')
        return (saved as PersonaType) || 'coaching'
    })
    const [showDetail, setShowDetail] = useState(false)
    const [showPersonaInfo, setShowPersonaInfo] = useState(false)

    const handlePersonaChange = React.useCallback((persona: PersonaType) => {
        setSelectedPersona(persona)
        localStorage.setItem('ai_feedback_persona', persona)
        setShowDetail(false) // ペルソナ切り替え時は要約に戻す
    }, [])

    const currentFeedback = feedback[selectedPersona]

    return (
        <div className="bg-gray-900/70 border border-gray-700 rounded-xl overflow-hidden shadow-sm">
            <button
                onClick={() => setShowPersonaInfo(!showPersonaInfo)}
                className="w-full bg-gray-800/50 px-4 py-3 border-b border-gray-700 flex items-center gap-2 hover:bg-gray-800/70 transition-colors cursor-pointer"
            >
                <Award className="w-5 h-5 text-indigo-400" />
                <h4 className="font-bold text-indigo-300 text-sm">AI Coach Feedback</h4>
                <span className="ml-auto text-xs text-gray-400">タップして説明を表示</span>
            </button>

            {/* ペルソナ説明 */}
            {showPersonaInfo && (
                <div className="px-4 py-3 bg-indigo-900/20 border-b border-indigo-800/50 animate-in slide-in-from-top-2 duration-200">
                    <p className="text-xs text-indigo-200 mb-3">3つのAIペルソナから選択できます:</p>
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <span className="text-xl shrink-0">{PERSONA_CONFIG.coaching.icon}</span>
                            <div>
                                <p className="text-sm font-bold text-indigo-300">{PERSONA_CONFIG.coaching.label}</p>
                                <p className="text-xs text-gray-300">{PERSONA_DESCRIPTIONS.coaching}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-xl shrink-0">{PERSONA_CONFIG.prosody.icon}</span>
                            <div>
                                <p className="text-sm font-bold text-purple-300">{PERSONA_CONFIG.prosody.label}</p>
                                <p className="text-xs text-gray-300">{PERSONA_DESCRIPTIONS.prosody}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-xl shrink-0">{PERSONA_CONFIG.analytical.icon}</span>
                            <div>
                                <p className="text-sm font-bold text-pink-300">{PERSONA_CONFIG.analytical.label}</p>
                                <p className="text-xs text-gray-300">{PERSONA_DESCRIPTIONS.analytical}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-xl shrink-0">{PERSONA_CONFIG.liaison.icon}</span>
                            <div>
                                <p className="text-sm font-bold text-cyan-300">{PERSONA_CONFIG.liaison.label}</p>
                                <p className="text-xs text-gray-300">{PERSONA_DESCRIPTIONS.liaison}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ペルソナ選択 */}
            <div className="p-3 border-b border-gray-700 bg-gray-800/30">
                <div className="flex gap-2">
                    <div className={`transition-opacity ${selectedPersona === 'coaching' ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handlePersonaChange('coaching')}
                            icon={<span className="text-2xl">{PERSONA_CONFIG.coaching.icon}</span>}
                            label={PERSONA_CONFIG.coaching.label}
                        />
                    </div>
                    <div className={`transition-opacity ${selectedPersona === 'prosody' ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handlePersonaChange('prosody')}
                            icon={<span className="text-2xl">{PERSONA_CONFIG.prosody.icon}</span>}
                            label={PERSONA_CONFIG.prosody.label}
                        />
                    </div>
                    <div className={`transition-opacity ${selectedPersona === 'analytical' ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handlePersonaChange('analytical')}
                            icon={<span className="text-2xl">{PERSONA_CONFIG.analytical.icon}</span>}
                            label={PERSONA_CONFIG.analytical.label}
                        />
                    </div>
                    <div className={`transition-opacity ${selectedPersona === 'liaison' ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handlePersonaChange('liaison')}
                            icon={<span className="text-2xl">{PERSONA_CONFIG.liaison.icon}</span>}
                            label={PERSONA_CONFIG.liaison.label}
                        />
                    </div>
                </div>
            </div>

            {/* 要約表示 */}
            <div className="p-4 space-y-2">
                <h5 className="text-sm font-bold text-gray-200 mb-2">📝 要約</h5>
                <ul className="space-y-1.5">
                    {currentFeedback.summary.points.map((point, i) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-300">
                            <span className="text-indigo-400 font-bold shrink-0">•</span>
                            <span>{point}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* 詳細表示トグル */}
            <div className="border-t border-gray-700">
                <button
                    onClick={() => setShowDetail(!showDetail)}
                    className="w-full px-4 py-2 bg-gray-800/50 hover:bg-gray-700/50 text-gray-200 text-sm font-medium transition-all duration-200 flex items-center justify-between"
                >
                    <span>詳細を{showDetail ? '閉じる' : '表示'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDetail ? 'rotate-180' : ''}`} />
                </button>

                {showDetail && (
                    <div className="p-4 space-y-4 animate-in slide-in-from-top-2 duration-200 bg-gray-900/30">
                        {selectedPersona === 'coaching' && (
                            <>
                                <div>
                                    <h6 className="text-xs font-bold text-green-400 mb-1">✨ 総合評価</h6>
                                    <p className="text-sm text-gray-300">{feedback.coaching.overall_score_display}</p>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-green-400 mb-1">👏 良かった点</h6>
                                    <p className="text-sm text-gray-300">{feedback.coaching.praise}</p>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-yellow-400 mb-1">🎯 改善ポイント TOP3</h6>
                                    <div className="space-y-2">
                                        {feedback.coaching.improvement_areas.map((area, i) => (
                                            <div key={i} className="bg-gray-800/50 rounded p-2 border border-gray-700">
                                                <p className="text-sm font-bold text-yellow-300 mb-1">{i + 1}. {area.word}</p>
                                                <p className="text-xs text-gray-400 mb-1">原因: {area.cause}</p>
                                                <p className="text-xs text-blue-300">練習法: {area.practice}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-blue-400 mb-1">💡 アドバイス</h6>
                                    <p className="text-sm text-gray-300">{feedback.coaching.general_advice}</p>
                                </div>
                            </>
                        )}

                        {selectedPersona === 'prosody' && (
                            <>
                                <div>
                                    <h6 className="text-xs font-bold text-purple-400 mb-1">🎼 リズム分析</h6>
                                    <p className="text-sm text-gray-300">{feedback.prosody.rhythm_analysis}</p>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-purple-400 mb-1">⏸️ 間の取り方</h6>
                                    <ul className="space-y-1">
                                        {feedback.prosody.break_advice.map((advice, i) => (
                                            <li key={i} className="flex gap-2 text-sm text-gray-300">
                                                <span className="text-purple-400 shrink-0">•</span>
                                                <span>{advice}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-purple-400 mb-1">🎶 抑揚・強弱</h6>
                                    <ul className="space-y-1">
                                        {feedback.prosody.intonation_advice.map((advice, i) => (
                                            <li key={i} className="flex gap-2 text-sm text-gray-300">
                                                <span className="text-purple-400 shrink-0">•</span>
                                                <span>{advice}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h6 className="text-xs font-bold text-purple-400 mb-1">🎭 演技的視点</h6>
                                    <p className="text-sm text-gray-300">{feedback.prosody.musical_advice}</p>
                                </div>
                            </>
                        )}

                        {selectedPersona === 'analytical' && (
                            <>
                                {feedback.analytical.mispronunciations.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-red-400 mb-1">❌ 誤発音リスト</h6>
                                        <div className="space-y-1">
                                            {feedback.analytical.mispronunciations.map((item, i) => (
                                                <div key={i} className="bg-red-900/20 rounded p-2 border border-red-800/50 text-xs">
                                                    <span className="font-bold text-red-300">{item.word}</span>
                                                    <span className="text-gray-400"> - 音素: {item.error_phoneme}</span>
                                                    <span className="text-gray-400"> (スコア: {item.score})</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {feedback.analytical.omissions.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-purple-400 mb-1">⚠️ 省略された単語</h6>
                                        <p className="text-sm text-gray-300">{feedback.analytical.omissions.join(', ')}</p>
                                    </div>
                                )}
                                {feedback.analytical.insertions.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-orange-400 mb-1">➕ 余分な単語</h6>
                                        <p className="text-sm text-gray-300">{feedback.analytical.insertions.join(', ')}</p>
                                    </div>
                                )}
                                {feedback.analytical.prosody_errors.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-pink-400 mb-1">🎵 韻律エラー</h6>
                                        <div className="space-y-1">
                                            {feedback.analytical.prosody_errors.map((error, i) => (
                                                <div key={i} className="bg-pink-900/20 rounded p-2 border border-pink-800/50 text-xs">
                                                    <span className="font-bold text-pink-300">{error.word}</span>
                                                    <span className="text-gray-400"> - {error.error_type}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {selectedPersona === 'liaison' && (
                            <>
                                <div>
                                    <h6 className="text-xs font-bold text-cyan-400 mb-1">🔗 リンキング全体の評価</h6>
                                    <p className="text-sm text-gray-300">{feedback.liaison.liaison_analysis}</p>
                                </div>
                                {feedback.liaison.successful_liaisons.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-green-400 mb-1">✅ 成功したリエゾン</h6>
                                        <div className="space-y-2">
                                            {feedback.liaison.successful_liaisons.map((item, i) => (
                                                <div key={i} className="bg-green-900/20 rounded p-2 border border-green-800/50">
                                                    <p className="text-sm font-bold text-green-300">{item.words}</p>
                                                    <p className="text-xs text-gray-400">間隔: {item.gap_ms}ms</p>
                                                    {item.comment && <p className="text-xs text-cyan-300 mt-1">{item.comment}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {feedback.liaison.liaison_opportunities.length > 0 && (
                                    <div>
                                        <h6 className="text-xs font-bold text-yellow-400 mb-1">💡 改善のチャンス</h6>
                                        <div className="space-y-2">
                                            {feedback.liaison.liaison_opportunities.map((item, i) => (
                                                <div key={i} className="bg-yellow-900/20 rounded p-2 border border-yellow-800/50">
                                                    <p className="text-sm font-bold text-yellow-300">{item.words}</p>
                                                    <p className="text-xs text-gray-400">間隔: {item.gap_ms}ms</p>
                                                    {item.suggestion && <p className="text-xs text-blue-300 mt-1">💡 {item.suggestion}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <h6 className="text-xs font-bold text-cyan-400 mb-1">🔄 スコアの再解釈</h6>
                                    <p className="text-sm text-gray-300">{feedback.liaison.score_reinterpretation}</p>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

type GradingResultDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    result: GradeRecordingResponse | null
    userAudioBlob?: Blob
    referenceAudioUrl?: string
    referenceWords?: WordResponse[]
    segments?: SegmentResponse[]
}

// レーダーチャートコンポーネント
const RadarChart = ({ scores }: { scores: Record<string, number> }) => {
    // PronScoreの色はスコアに応じて変更
    const pronScoreColor = React.useMemo(() => {
        const score = scores['PronScore'] || 0
        if (score >= 80) return '#10b981' // green-500
        if (score >= 60) return '#eab308' // yellow-500
        return '#ef4444' // red-500
    }, [scores])

    const metrics = React.useMemo(() => [
        { key: 'PronScore', label: 'Pronunciation', color: pronScoreColor, fillColor: 'rgba(16, 185, 129, 0.3)' },
        { key: 'AccuracyScore', label: 'Accuracy', color: '#34d399', fillColor: 'rgba(52, 211, 153, 0.3)' }, // green
        { key: 'FluencyScore', label: 'Fluency', color: '#60a5fa', fillColor: 'rgba(96, 165, 250, 0.3)' }, // blue
        { key: 'ProsodyScore', label: 'Prosody', color: '#c084fc', fillColor: 'rgba(192, 132, 252, 0.3)' }, // purple
        { key: 'CompletenessScore', label: 'Completeness', color: '#fb923c', fillColor: 'rgba(251, 146, 60, 0.3)' }, // orange
    ], [pronScoreColor])

    const size = 200
    const center = size / 2
    const radius = 65
    const angleStep = (Math.PI * 2) / metrics.length

    const getPoint = React.useCallback((index: number, value: number) => {
        const angle = index * angleStep - Math.PI / 2
        const r = (value / 100) * radius
        const x = center + r * Math.cos(angle)
        const y = center + r * Math.sin(angle)
        return { x, y }
    }, [angleStep, center, radius])

    const ticks = React.useMemo(() => [20, 40, 60, 80, 100], [])

    const polygonPoints = React.useMemo(() => metrics.map((m, i) => {
        const { x, y } = getPoint(i, scores[m.key] || 0)
        return `${x},${y}`
    }).join(' '), [metrics, getPoint, scores])

    return (
        <div className="flex justify-center py-4">
            <svg width={size} height={size} className="overflow-visible">
                {/* 背景グリッド */}
                {ticks.map((tick) => (
                    <polygon
                        key={tick}
                        points={metrics.map((_, i) => {
                            const { x, y } = getPoint(i, tick)
                            return `${x},${y}`
                        }).join(' ')}
                        fill="none"
                        stroke="#374151"
                        strokeWidth="1"
                    />
                ))}

                {/* 軸 */}
                {metrics.map((_, i) => {
                    const { x, y } = getPoint(i, 100)
                    return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke="#374151" strokeWidth="1" />
                })}

                {/* スコア領域 */}
                <polygon points={polygonPoints} fill="rgba(99, 102, 241, 0.3)" stroke="#818cf8" strokeWidth="2" />

                {/* ドットとラベル */}
                {metrics.map((m, i) => {
                    const { x, y } = getPoint(i, scores[m.key] || 0)
                    const labelDist = 1.35
                    const lx = center + (radius * labelDist) * Math.cos(i * angleStep - Math.PI / 2)
                    const ly = center + (radius * labelDist) * Math.sin(i * angleStep - Math.PI / 2)

                    return (
                        <g key={m.key}>
                            <circle cx={x} cy={y} r="4" fill="#818cf8" />
                            <text
                                x={lx}
                                y={ly - 6}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-[10px] font-bold"
                                fill={m.color}
                            >
                                {m.label}
                            </text>
                            <text
                                x={lx}
                                y={ly + 6}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-[12px] font-bold"
                                fill={m.color}
                            >
                                {Math.round(scores[m.key])}
                            </text>
                        </g>
                    )
                })}
            </svg>
        </div>
    )
}

// 音素詳細パネルコンポーネント
const PhonemeBreakdownPanel = ({
    wordData,
    onClose,
    userAudioBlob,
    referenceAudioUrl,
    referenceWordTiming,
}: {
    wordData: WordDetail
    onClose: () => void
    userAudioBlob?: Blob
    referenceAudioUrl?: string
    referenceWordTiming?: WordResponse
}) => {
    const [showSymbol, setShowSymbol] = React.useState(() => {
        const saved = localStorage.getItem('phonemeDisplayMode')
        return saved === 'symbol'
    })

    const toggleDisplay = React.useCallback(() => {
        const newValue = !showSymbol
        setShowSymbol(newValue)
        localStorage.setItem('phonemeDisplayMode', newValue ? 'symbol' : 'id')
    }, [showSymbol])

    const phonemeMapping = React.useMemo(() => ({
        // Vowels - Monophthongs
        "iy": { symbol: "i", description: "唇を横に引いて強く「イー」" },
        "ih": { symbol: "ɪ", description: "「イ」と「エ」の中間、短く" },
        "eh": { symbol: "e", description: "日本語の「エ」に近い" },
        "ae": { symbol: "æ", description: "「ア」と「エ」を混ぜて潰した音" },
        "aa": { symbol: "ɑ", description: "喉の奥を開けて深く「アー」" },
        "ao": { symbol: "ɔ", description: "口を丸めて「オー」" },
        "uh": { symbol: "ʊ", description: "唇を少し丸めて短く「ウ」" },
        "uw": { symbol: "u", description: "唇を強く突き出して「ウー」" },
        "ah": { symbol: "ʌ", description: "口をあまり開けず短く「ア」" },
        "ax": { symbol: "ə", description: "Schwa（あいまい母音）。弱く「ア」" },
        "er": { symbol: "ɝ", description: "舌を巻いた強い「アー」（R音性母音）" },
        // Vowels - Diphthongs
        "ey": { symbol: "eɪ", description: "「エィ」" },
        "ay": { symbol: "aɪ", description: "「アィ」" },
        "oy": { symbol: "ɔɪ", description: "「オィ」" },
        "aw": { symbol: "aʊ", description: "「アゥ」" },
        "ow": { symbol: "oʊ", description: "「オゥ」（米語的）" },
        // Consonants - Plosives
        "p": { symbol: "p", description: "無声・唇を閉じて弾く" },
        "b": { symbol: "b", description: "有声・唇を閉じて弾く" },
        "t": { symbol: "t", description: "無声・舌先を歯茎につけて弾く" },
        "d": { symbol: "d", description: "有声・舌先を歯茎につけて弾く" },
        "k": { symbol: "k", description: "無声・喉の奥で弾く" },
        "g": { symbol: "g", description: "有声・喉の奥で弾く" },
        // Consonants - Fricatives
        "f": { symbol: "f", description: "無声・上の歯を下唇に乗せる" },
        "v": { symbol: "v", description: "有声・上の歯を下唇に乗せる" },
        "th": { symbol: "θ", description: "無声・舌を歯で軽く挟む（TH）" },
        "dh": { symbol: "ð", description: "有声・舌を歯で軽く挟む（TH）" },
        "s": { symbol: "s", description: "無声・歯擦音" },
        "z": { symbol: "z", description: "有声・歯擦音" },
        "sh": { symbol: "ʃ", description: "無声・静かにの「シー」" },
        "zh": { symbol: "ʒ", description: "有声・「ジ」に近い摩擦音" },
        "h": { symbol: "h", description: "無声・息を吐く音" },
        // Consonants - Affricates
        "ch": { symbol: "tʃ", description: "無声・「チ」" },
        "jh": { symbol: "dʒ", description: "有声・「ヂ」" },
        // Consonants - Nasals
        "m": { symbol: "m", description: "唇を閉じて鼻へ抜く" },
        "n": { symbol: "n", description: "舌を歯茎につけて鼻へ抜く" },
        "ng": { symbol: "ŋ", description: "舌の奥を持ち上げて鼻へ抜く（ング）" },
        // Consonants - Liquids & Glides
        "l": { symbol: "l", description: "舌先を歯茎につける" },
        "r": { symbol: "r", description: "舌を巻く、どこにも触れない" },
        "w": { symbol: "w", description: "唇を丸めてすぐに広げる" },
        "y": { symbol: "j", description: "「ィ」から始める音" }
    } as Record<string, { symbol: string; description: string }>), [])

    const errorTypeDescriptions = React.useMemo(() => ({
        None: 'エラーなし',
        Omission: '省略: 単語が発音されませんでした',
        Insertion: '挿入: 余分な単語が発音されました',
        Mispronunciation: '誤発音: 発音が不正確です (AccuracyScore < 60)',
        UnexpectedBreak: '不適切な区切り: 不要な間が入りました',
        MissingBreak: '区切り不足: 必要な間がありません',
        Monotone: '単調: 抑揚が平坦、上昇、または下降しています'
    } as Record<string, string>), [])

    const errorType = wordData.PronunciationAssessment.ErrorType
    const errorDescription = React.useMemo(() =>
        errorType && errorType !== 'None' ? errorTypeDescriptions[errorType] || errorType : null,
        [errorType, errorTypeDescriptions]
    )

    const getPhonemeDisplay = React.useCallback((phonemeId: string) => {
        const mapping = phonemeMapping[phonemeId.toLowerCase()]
        if (showSymbol && mapping) {
            return mapping.symbol
        }
        return phonemeId
    }, [phonemeMapping, showSymbol])

    // 単語の発音サンプルを再生
    const playWordSample = React.useCallback(() => {
        if ('speechSynthesis' in window) {
            // 既存の発話を停止
            window.speechSynthesis.cancel()

            const utterance = new SpeechSynthesisUtterance(wordData.Word)
            utterance.lang = 'en-US' // 英語（アメリカ）
            utterance.rate = 0.8 // 少しゆっくり
            utterance.pitch = 1.0

            window.speechSynthesis.speak(utterance)
        }
    }, [wordData.Word])

    return (
        <div className="mt-4 bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-xl p-4 shadow-2xl border border-gray-700 animate-in slide-in-from-top-2 duration-200 overflow-hidden">
            <div className="flex justify-between items-start mb-3 border-b border-gray-700 pb-2">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-xl font-bold capitalize">{wordData.Word}</span>
                        <button
                            onClick={playWordSample}
                            className="group relative p-1 hover:bg-blue-500/10 rounded-full transition-all duration-200"
                            title="単語の発音を聞く"
                        >
                            <Volume2 className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors" />
                        </button>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${wordData.PronunciationAssessment.AccuracyScore >= 80 ? 'bg-green-600 text-white' :
                            wordData.PronunciationAssessment.AccuracyScore >= 60 ? 'bg-yellow-600 text-white' : 'bg-red-600 text-white'
                            }`}>
                            Score: {Math.round(wordData.PronunciationAssessment.AccuracyScore)}
                        </span>
                    </div>
                    {errorDescription && (
                        <div className="mt-2 bg-red-900/30 border border-red-700/50 rounded-lg p-2">
                            <div className="flex items-center gap-1 text-red-400 text-xs font-bold mb-1">
                                <AlertCircle className="w-3 h-3" />
                                <span>{errorType}</span>
                            </div>
                            <p className="text-xs text-red-300">{errorDescription}</p>
                        </div>
                    )}
                </div>
                <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded-full text-gray-400">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* 表示切り替えボタン */}
            <div className="mb-3 flex justify-center">
                <button
                    onClick={toggleDisplay}
                    className="text-xs px-3 py-1.5 bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-700/50 rounded-full text-indigo-300 transition-colors"
                >
                    {showSymbol ? 'ID表示に切り替え' : 'IPA記号で表示'}
                </button>
            </div>

            {/* 音素リスト - 横スクロール可能 */}
            <div className="overflow-x-auto pb-2 -mx-4 px-4">
                <div className="flex gap-1">
                    {wordData.Phonemes?.map((pho, idx) => (
                        <div key={idx} className="flex flex-col items-center p-2 shrink-0">
                            <span className={`text-sm font-bold mb-1 ${pho.PronunciationAssessment.AccuracyScore >= 80 ? 'text-green-400' :
                                pho.PronunciationAssessment.AccuracyScore >= 60 ? 'text-yellow-400' : 'text-red-400'
                                }`}
                                style={{ fontFamily: "Gentium Plus" }}
                            >
                                {getPhonemeDisplay(pho.Phoneme)}
                            </span>

                            {/* 縦棒グラフ */}
                            <div className="h-10 w-2 bg-gray-950 rounded-full relative mb-1 overflow-hidden">
                                <div
                                    className={`absolute bottom-0 w-full rounded-full transition-all duration-500 ${pho.PronunciationAssessment.AccuracyScore >= 80 ? 'bg-green-500' :
                                        pho.PronunciationAssessment.AccuracyScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                    style={{ height: `${pho.PronunciationAssessment.AccuracyScore}%` }}
                                />
                            </div>
                            <span className="text-[10px] text-gray-400 font-mono">
                                {Math.round(pho.PronunciationAssessment.AccuracyScore)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">音素ごとのスコア (Phoneme Scores) - スワイプでスクロール →</p>

            {/* 発音比較機能 */}
            {userAudioBlob && referenceAudioUrl && (
                <PronunciationComparison
                    wordData={wordData}
                    userAudioBlob={userAudioBlob}
                    referenceAudioUrl={referenceAudioUrl}
                    referenceWordTiming={referenceWordTiming}
                />
            )}
        </div>
    )
}

// インタラクティブ文章表示コンポーネント
const InteractiveSentence = ({
    words,
    displayText,
    userAudioBlob,
    referenceAudioUrl,
    referenceWords,
}: {
    words: WordDetail[]
    displayText: string
    userAudioBlob?: Blob
    referenceAudioUrl?: string
    referenceWords?: WordResponse[]
    segments?: SegmentResponse[]
}) => {
    const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null)
    const panelRef = React.useRef<HTMLDivElement>(null)

    // アライメント結果をメモ化
    const alignment = React.useMemo(() => {
        if (!referenceWords || referenceWords.length === 0) return []
        return speechAligner.align(referenceWords, words)
    }, [referenceWords, words])

    // referenceWordsから対応する単語を探す（アライメント結果を使用）
    const findReferenceWord = (wordDetail: WordDetail): WordResponse | undefined => {
        if (!referenceWords || referenceWords.length === 0 || alignment.length === 0) return undefined

        // アライメント結果から該当するhypothesisを探す
        for (const item of alignment) {
            if (item.hyp && item.hyp === wordDetail) {
                // MATCHまたはSUBSTITUTIONの場合、対応するrefを返す
                if ((item.type === 'MATCH' || item.type === 'SUBSTITUTION') && item.ref) {
                    return item.ref
                }
                break
            }
        }

        return undefined
    }

    const handleWordClick = React.useCallback((index: number) => {
        setSelectedWordIndex(prev => prev === index ? null : index)

        // パネルが表示される場合、少し遅延してスクロール
        if (selectedWordIndex !== index) {
            setTimeout(() => {
                panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }, 100)
        }
    }, [selectedWordIndex])

    // DisplayTextから文の区切り位置を判断
    const sentenceBreaks = React.useMemo(() => {
        const breaks = new Set<number>()

        // DisplayTextを正規化（小文字化、句読点も保持）
        const normalizedDisplay = displayText.toLowerCase()
        let displayIndex = 0

        words.forEach((word, wordIndex) => {
            const normalizedWord = word.Word.toLowerCase()

            // DisplayText内で現在の単語を検索
            const wordPos = normalizedDisplay.indexOf(normalizedWord, displayIndex)

            if (wordPos !== -1) {
                // 単語の直後に句読点があるかチェック
                const afterWordPos = wordPos + normalizedWord.length
                const nextChar = normalizedDisplay[afterWordPos]

                if (nextChar && /[.!?。！？]/.test(nextChar)) {
                    breaks.add(wordIndex)
                }

                displayIndex = afterWordPos
            }
        })

        return breaks
    }, [displayText, words])

    const getWordStyle = React.useCallback((word: WordDetail) => {
        const errorType = word.PronunciationAssessment.ErrorType

        let baseColor = 'text-gray-200'
        let bgColor = ''
        let decor = ''

        // ErrorTypeに基づくスタイル設定
        switch (errorType) {
            case 'Mispronunciation':
                baseColor = 'text-red-400'
                decor = 'underline decoration-red-500 decoration-2 underline-offset-4'
                break
            case 'Omission':
                baseColor = 'text-purple-400'
                bgColor = 'bg-purple-900/20'
                decor = 'line-through decoration-purple-500 decoration-2'
                break
            case 'Insertion':
                baseColor = 'text-orange-400'
                bgColor = 'bg-orange-900/20'
                decor = 'underline decoration-orange-500 decoration-wavy decoration-2 underline-offset-4'
                break
            case 'UnexpectedBreak':
                baseColor = 'text-cyan-400'
                bgColor = 'bg-cyan-900/20'
                decor = 'border-l-2 border-r-2 border-cyan-500'
                break
            case 'MissingBreak':
                baseColor = 'text-pink-400'
                bgColor = 'bg-pink-900/20'
                break
            case 'Monotone':
                baseColor = 'text-blue-400'
                bgColor = 'bg-blue-900/20'
                decor = 'italic'
                break
            case 'None':
            default:
                baseColor = 'text-gray-200'
                break
        }

        return { baseColor, bgColor, decor }
    }, [])

    return (
        <div className="space-y-2 overflow-x-hidden">
            <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                <span className="inline-block w-4 h-4 bg-indigo-900/50 text-indigo-400 rounded-full text-center leading-4 font-bold border border-indigo-700">!</span>
                単語をタップして詳細を確認
            </p>

            {/* エラータイプ凡例 */}
            <div className="bg-gray-900/50 p-3 rounded-lg border border-gray-700 text-xs">
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-red-400 underline decoration-red-500 decoration-2">赤下線</span>
                        <span className="text-gray-400">: 誤発音</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-purple-400 line-through decoration-purple-500">紫取消</span>
                        <span className="text-gray-400">: 省略</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-orange-400 underline decoration-orange-500 decoration-wavy">橙波線</span>
                        <span className="text-gray-400">: 挿入</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-cyan-400 border-l-2 border-r-2 border-cyan-500 px-1">水色枠</span>
                        <span className="text-gray-400">: 不要な区切り</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-pink-400 bg-pink-900/20 px-1 rounded">桃背景</span>
                        <span className="text-gray-400">: 区切り不足</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-blue-400 italic">青斜体</span>
                        <span className="text-gray-400">: 単調</span>
                    </div>
                </div>
            </div>

            {/* 文章表示エリア */}
            <div className="bg-gray-900/70 p-4 rounded-xl border border-gray-700 shadow-sm">
                <div className="flex flex-wrap gap-x-1.5 gap-y-1 text-lg leading-relaxed">
                    {words.map((word, index) => {
                        const isSelected = selectedWordIndex === index
                        const { baseColor, bgColor, decor } = getWordStyle(word)
                        const shouldBreak = sentenceBreaks.has(index)

                        return (
                            <React.Fragment key={index}>
                                <span
                                    onClick={() => handleWordClick(index)}
                                    className={`
                                        relative cursor-pointer rounded-md transition-all duration-200
                                        ${baseColor} ${bgColor} ${decor}
                                        ${isSelected ? 'bg-indigo-600 text-white ring-2 ring-indigo-400 decoration-transparent shadow-md transform scale-105 z-10' : 'hover:bg-gray-800/50'}
                                    `}
                                >
                                    {word.Word}
                                </span>
                                {shouldBreak && <div className="w-full" />}
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>

            {/* 詳細パネル */}
            {selectedWordIndex !== null && (
                <div ref={panelRef}>
                    <PhonemeBreakdownPanel
                        wordData={words[selectedWordIndex]}
                        onClose={() => setSelectedWordIndex(null)}
                        userAudioBlob={userAudioBlob}
                        referenceAudioUrl={referenceAudioUrl}
                        referenceWordTiming={findReferenceWord(words[selectedWordIndex])}
                    />
                </div>
            )}
        </div>
    )
}

export const GradingResultDialog = ({
    open,
    onOpenChange,
    result,
    userAudioBlob,
    referenceAudioUrl,
    referenceWords,
    segments,
}: GradingResultDialogProps) => {
    console.log('[GradingResultDialog] Rendered with props:', {
        open,
        hasResult: !!result,
        result: result ? 'exists' : 'null',
        resultType: typeof result
    })

    const [selectedScoreInfo, setSelectedScoreInfo] = useState<string | null>(null)
    const [showSegmentComparison, setShowSegmentComparison] = useState(false)
    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0)
    const [segmentComparisonKey, setSegmentComparisonKey] = useState(0)

    // セグメント選択変更時に再生を停止するためのキーを更新
    const handleSegmentChange = React.useCallback((index: number) => {
        setSelectedSegmentIndex(index)
        setSegmentComparisonKey(prev => prev + 1) // キーを変更してコンポーネントを再マウント
    }, [])

    // スコア説明を事前に定義
    const scoreDescriptions = React.useMemo(() => ({
        PronScore: {
            title: '発音スコア (PronScore)',
            description: '特定の音声の発音品質の全体的なスコア。AccuracyScore、FluencyScore、CompletenessScore、ProsodyScoreから重み付けで計算されます。',
            level: '全文レベル'
        },
        AccuracyScore: {
            title: '正確性 (Accuracy)',
            description: '音声の発音精度。音素がネイティブスピーカーの発音とどれだけ厳密に一致しているかを示します。',
            level: '音素/音節/単語/全文レベル'
        },
        FluencyScore: {
            title: '流暢性 (Fluency)',
            description: '指定された音声の流暢性。音声がネイティブスピーカーによる単語間の間の取り方にどれだけ厳密に一致しているかを示します。',
            level: '全文レベル'
        },
        ProsodyScore: {
            title: '韻律 (Prosody)',
            description: '指定された音声の韻律。アクセント、イントネーション、話す速度、リズムなど、指定された音声がどの程度自然であるかを示します。',
            level: '全文レベル'
        },
        CompletenessScore: {
            title: '完全性 (Completeness)',
            description: '音声の完成度。入力参照テキストに対する発音された単語の比率によって計算されます。',
            level: '全文レベル'
        }
    } as Record<string, { title: string; description: string; level: string }>), [])

    // ReferenceTextを文に分割（送信したテキストを文単位で分割）
    const referenceSentences = React.useMemo(() => {
        if (!result) return []

        const referenceText = result.raw_response.ReferenceText
        if (!referenceText) return []

        // 文の区切り記号で分割（. ! ? および日本語の句読点）
        const sentences = referenceText
            .split(/(?<=[.!?。！？])\s+/)
            .map(s => s.trim())
            .filter(s => s.length > 0)

        return sentences
    }, [result])

    // DisplayTextに対応するセグメントのみをフィルタリング（無音を除く）
    const filteredSegments = React.useMemo(() => {
        if (!segments || segments.length === 0 || !result) return []

        // DisplayTextを小文字化して正規化
        const normalizedDisplayText = result.raw_response.DisplayText.toLowerCase().replace(/[^\w\s]/g, '')
        const displayWords = normalizedDisplayText.split(/\s+/).filter(w => w.length > 0)

        return segments.filter(segment => {
            // 無音セグメントを除外
            if (segment.skipped || segment.no_speech_prob > 0.8) {
                return false
            }

            // セグメントのテキストを正規化
            const normalizedSegmentText = segment.text.toLowerCase().replace(/[^\w\s]/g, '')
            const segmentWords = normalizedSegmentText.split(/\s+/).filter(w => w.length > 0)

            // セグメントの単語がDisplayTextに含まれているかチェック
            return segmentWords.some(segWord =>
                displayWords.some(dispWord =>
                    dispWord.includes(segWord) || segWord.includes(dispWord)
                )
            )
        })
    }, [segments, result])

    if (!result) {
        return null
    }

    const mainResult = result.raw_response.NBest[0]
    const overallScores = mainResult.PronunciationAssessment
    const mainScore = Math.round(overallScores.PronScore)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 bg-gray-900 border-gray-700"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogTitle className="sr-only">発音評価結果</DialogTitle>
                <DialogDescription className="sr-only">
                    音声認識と発音評価の詳細結果。スコア、単語ごとの詳細、AIフィードバックが含まれます。
                </DialogDescription>
                <div className="bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 overflow-x-hidden">
                    {/* ヘッダー部分 */}
                    <div className="p-6 border-b border-gray-700">
                        <h2 className="text-xl font-bold text-gray-100 mb-4">採点結果</h2>

                        {/* スコア表示 */}
                        <div className="flex items-center gap-4 mb-4">
                            {/* メインスコア */}
                            <div
                                onClick={() => setSelectedScoreInfo(selectedScoreInfo === 'PronScore' ? null : 'PronScore')}
                                className={`flex flex-col items-center justify-center w-20 h-20 rounded-xl font-bold text-2xl shrink-0 border-2 cursor-pointer transition-transform hover:scale-105 ${mainScore >= 80 ? 'bg-green-900/30 text-green-400 border-green-700' :
                                    mainScore >= 60 ? 'bg-yellow-900/30 text-yellow-400 border-yellow-700' :
                                        'bg-red-900/30 text-red-400 border-red-700'
                                    } ${selectedScoreInfo === 'PronScore' ? 'ring-2 ring-white/50' : ''}`}>
                                {mainScore}
                                <span className="text-xs font-normal -mt-0.5">PronScore</span>
                            </div>

                            {/* 詳細スコア */}
                            <div className="flex-1 grid grid-cols-2 gap-2">
                                <div
                                    onClick={() => setSelectedScoreInfo(selectedScoreInfo === 'FluencyScore' ? null : 'FluencyScore')}
                                    className={`bg-blue-900/30 px-3 py-2 rounded text-xs font-medium text-blue-300 flex justify-between border border-blue-800/50 cursor-pointer transition-transform hover:scale-105 ${selectedScoreInfo === 'FluencyScore' ? 'ring-2 ring-blue-400/50' : ''}`}>
                                    <span>Fluency</span>
                                    <span className="font-bold">{Math.round(overallScores.FluencyScore)}</span>
                                </div>
                                <div
                                    onClick={() => setSelectedScoreInfo(selectedScoreInfo === 'ProsodyScore' ? null : 'ProsodyScore')}
                                    className={`bg-purple-900/30 px-3 py-2 rounded text-xs font-medium text-purple-300 flex justify-between border border-purple-800/50 cursor-pointer transition-transform hover:scale-105 ${selectedScoreInfo === 'ProsodyScore' ? 'ring-2 ring-purple-400/50' : ''}`}>
                                    <span>Prosody</span>
                                    <span className="font-bold">{Math.round(overallScores.ProsodyScore)}</span>
                                </div>
                                <div
                                    onClick={() => setSelectedScoreInfo(selectedScoreInfo === 'AccuracyScore' ? null : 'AccuracyScore')}
                                    className={`bg-green-900/30 px-3 py-2 rounded text-xs font-medium text-green-300 flex justify-between border border-green-800/50 cursor-pointer transition-transform hover:scale-105 ${selectedScoreInfo === 'AccuracyScore' ? 'ring-2 ring-green-400/50' : ''}`}>
                                    <span>Accuracy</span>
                                    <span className="font-bold">{Math.round(overallScores.AccuracyScore)}</span>
                                </div>
                                <div
                                    onClick={() => setSelectedScoreInfo(selectedScoreInfo === 'CompletenessScore' ? null : 'CompletenessScore')}
                                    className={`bg-orange-900/30 px-3 py-2 rounded text-xs font-medium text-orange-300 flex justify-between border border-orange-800/50 cursor-pointer transition-transform hover:scale-105 ${selectedScoreInfo === 'CompletenessScore' ? 'ring-2 ring-orange-400/50' : ''}`}>
                                    <span>Complete</span>
                                    <span className="font-bold">{Math.round(overallScores.CompletenessScore)}</span>
                                </div>
                            </div>
                        </div>

                        {/* スコア説明パネル */}
                        {selectedScoreInfo && (
                            <div className="mt-4 bg-gray-800/70 border border-gray-700 rounded-lg p-4 animate-in slide-in-from-top-2 duration-200">
                                <h3 className="text-sm font-bold text-gray-100 mb-2">{scoreDescriptions[selectedScoreInfo].title}</h3>
                                <p className="text-xs text-gray-300 mb-2">{scoreDescriptions[selectedScoreInfo].description}</p>
                                <p className="text-xs text-gray-400">評価レベル: {scoreDescriptions[selectedScoreInfo].level}</p>
                            </div>
                        )}
                    </div>

                    {/* コンテンツ部分 */}
                    <div className="bg-gray-800/50">
                        {/* レーダーチャート */}
                        <div className="p-6 pb-0 bg-gray-900/50 border-b border-gray-700">
                            <h4 className="text-xs font-bold text-gray-400 uppercase text-center">Score Balance</h4>
                            <RadarChart scores={overallScores} />
                        </div>

                        <div className="p-6 space-y-6 overflow-x-hidden">
                            {/* 単語詳細 */}
                            <div className="overflow-x-hidden">
                                <h4 className="text-sm font-bold text-gray-200 mb-3 flex items-center gap-2">
                                    <Volume2 className="w-4 h-4 text-indigo-400" />
                                    Word & Phoneme Details
                                </h4>

                                {/* セグメント比較ボタン */}
                                {userAudioBlob && referenceAudioUrl && referenceWords && referenceSentences.length > 0 && (
                                    <div className="mb-4">
                                        <button
                                            onClick={() => setShowSegmentComparison(!showSegmentComparison)}
                                            className="w-full px-3 py-2 bg-gray-800/70 hover:bg-gray-700/70 text-gray-200 rounded-lg text-sm font-medium transition-all duration-200 border border-gray-600! flex items-center justify-between"
                                        >
                                            <span>文単位で発音を比較</span>
                                            <svg
                                                className={`w-4 h-4 transition-transform duration-200 ${showSegmentComparison ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {showSegmentComparison && (
                                            <div className="mt-2 space-y-2 animate-in slide-in-from-top-2 duration-200">
                                                {/* セグメント選択 */}
                                                <div className="bg-gray-800/50 rounded-lg p-2 border border-gray-700">
                                                    <p className="text-xs text-gray-400 mb-1.5 px-1">文を選択:</p>
                                                    <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                                        {referenceSentences.map((sentence, index) => (
                                                            <div
                                                                key={index}
                                                                onClick={() => handleSegmentChange(index)}
                                                                className={`flex items-start gap-2 p-1.5 rounded cursor-pointer transition-all duration-150 ${selectedSegmentIndex === index
                                                                    ? 'bg-blue-600/20 border-l-3 border-blue-500'
                                                                    : 'hover:bg-gray-700/30 border-l-3 border-transparent'
                                                                    }`}
                                                            >
                                                                <span className={`shrink-0 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${selectedSegmentIndex === index
                                                                    ? 'bg-blue-600 text-white'
                                                                    : 'bg-gray-700 text-gray-400'
                                                                    }`}>
                                                                    {index + 1}
                                                                </span>
                                                                <span className={`text-xs leading-snug ${selectedSegmentIndex === index
                                                                    ? 'text-blue-200 font-medium'
                                                                    : 'text-gray-300'
                                                                    }`}>
                                                                    {sentence}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* セグメント比較パネル */}
                                                    {filteredSegments[selectedSegmentIndex] && (() => {
                                                        const selectedSegment = filteredSegments[selectedSegmentIndex]
                                                        // セグメントの時間範囲内の単語のみをフィルタリング
                                                        const segmentWords = mainResult.Words.filter(word => {
                                                            const wordStartSeconds = word.Offset / 10_000_000
                                                            const wordEndSeconds = (word.Offset + word.Duration) / 10_000_000
                                                            // 単語の開始時刻または終了時刻がセグメント内にあればマッチ
                                                            return (
                                                                (wordStartSeconds >= selectedSegment.start && wordStartSeconds < selectedSegment.end) ||
                                                                (wordEndSeconds > selectedSegment.start && wordEndSeconds <= selectedSegment.end) ||
                                                                (wordStartSeconds <= selectedSegment.start && wordEndSeconds >= selectedSegment.end)
                                                            )
                                                        })
                                                        return (
                                                            <PronunciationComparison
                                                                key={segmentComparisonKey}
                                                                userAudioBlob={userAudioBlob}
                                                                referenceAudioUrl={referenceAudioUrl}
                                                                segmentMode={true}
                                                                segment={selectedSegment}
                                                                words={segmentWords}
                                                            />
                                                        )
                                                    })()}
                                                </div>


                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* 認識テキスト */}
                                <div className="mb-4 bg-gray-800/70 border border-gray-700 rounded-lg p-3">
                                    <p className="text-xs text-gray-400 mb-1 font-medium">認識されたテキスト (Recognized Text):</p>
                                    <p className="text-sm text-gray-200 leading-relaxed">{result.raw_response.DisplayText}</p>
                                </div>


                                <InteractiveSentence
                                    words={mainResult.Words}
                                    displayText={result.raw_response.DisplayText}
                                    userAudioBlob={userAudioBlob}
                                    referenceAudioUrl={referenceAudioUrl}
                                    referenceWords={referenceWords}
                                    segments={segments}
                                />
                            </div>

                            {/* AIフィードバック */}
                            {result.ai_feedback && (
                                <AIFeedbackPanel feedback={result.ai_feedback} />
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

